import { faComment } from '@fortawesome/free-regular-svg-icons';
import { faBoxArchive } from '@fortawesome/free-solid-svg-icons';
import {
  faArrowRotateRight,
  faArrowUpRightFromSquare,
  faFile,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Link, Module, ModuleBody } from '@wfp/react';
import React, { useEffect, useMemo } from 'react';
import { connect, useDispatch } from 'react-redux';

import { DisplayToggleWrapper } from 'src/components/DisplayWrapper';
import {
  COMMENTS_TARGET_ID,
  NARRATIVE,
  REPORT_DETAIL_DATA_KEY,
  SECTION_DETAIL_DATA_KEY,
  VERSIONS,
} from 'src/redux/constants';
import { getHistoryNarrative } from 'src/redux/narrative/actions';
import type { RootState } from 'src/redux/store';
import { changeViewStateAction } from 'src/redux/viewState/actions';
import { buildErrorComponents, sendGAEvent, showNotification } from 'src/utils';

interface SectionControlsProps {
  hasNarrative?: any;
  guidanceLink?: string;
  error?: any;
  dataSourceRefresh?: { isFetching?: boolean; error?: any };
  activeSectionID?: string | number;
  activeClickedTab?: string;
  reportState?: string;
  hasReport?: boolean;
  dataSourceRefreshAllowed?: boolean;
  handleRefreshDataSources: () => void;
}

interface RefreshDataSourceButtonProps {
  isFetching?: boolean;
  handleRefreshDataSources: () => void;
}

const RefreshDataSourceButton = ({
  isFetching,
  handleRefreshDataSources,
}: RefreshDataSourceButtonProps) => {
  const title = isFetching
    ? 'Refreshing data sources...'
    : 'Refresh data sources';

  return (
    <Button
      kind="ghost"
      onClick={() => {
        sendGAEvent('refresh_data_sources_btn_clicked');
        handleRefreshDataSources();
      }}
      title={title}
      data-test-id="refresh-data-sources-button"
      disabled={isFetching}
      iconReverse
      icon={<FontAwesomeIcon icon={faArrowRotateRight} />}
    >
      {title}
    </Button>
  );
};

export const SectionControlsComponent = ({
  hasNarrative,
  guidanceLink = '',
  error,
  dataSourceRefresh,
  activeSectionID,
  activeClickedTab,
  reportState,
  hasReport,
  dataSourceRefreshAllowed,
  handleRefreshDataSources,
}: SectionControlsProps) => {
  const dispatch = useDispatch();

  const errorsList = useMemo(() => {
    const commonErrorComponents = buildErrorComponents(error);
    const refreshDataSetErrorComponents = buildErrorComponents(
      dataSourceRefresh?.error,
    );

    const notifications = [];

    for (const errors of [
      commonErrorComponents,
      refreshDataSetErrorComponents,
    ]) {
      if (errors.length > 0) {
        notifications.push(errors);
      }
    }
    return notifications;
  }, [error, dataSourceRefresh?.error]);

  useEffect(() => {
    if (errorsList.length > 0) {
      showNotification(
        errorsList.map(error => error) as unknown as JSX.Element,
        'error',
        {
          autoClose: false,
          closeOnClick: false,
        },
      );
    }
  }, [errorsList]);

  function getNarrativeHistory() {
    dispatch(getHistoryNarrative(activeSectionID));
    dispatch(
      changeViewStateAction({
        activeClickedTab: VERSIONS,
        isTitleChanging: false,
        currentTitle: '',
      }),
    );
  }

  function onChangeNarrativeClicked() {
    dispatch(changeViewStateAction({ activeClickedTab: NARRATIVE }));
  }

  return (
    <Module className="section-controls">
      <ModuleBody>
        {!!hasNarrative && activeClickedTab === VERSIONS ? (
          <Button
            onClick={() => onChangeNarrativeClicked()}
            data-test-id="narrative-button"
            icon={<FontAwesomeIcon icon={faFile} />}
            kind="ghost"
            iconReverse
          >
            Narrative
          </Button>
        ) : (
          <Button
            kind="ghost"
            iconReverse
            onClick={() => {
              sendGAEvent('narrative_versions_btn_clicked');
              getNarrativeHistory();
            }}
            data-test-id="versions-button"
            icon={<FontAwesomeIcon icon={faBoxArchive} />}
          >
            Versions
          </Button>
        )}
        <DisplayToggleWrapper
          targetId={COMMENTS_TARGET_ID}
          component={Link}
          title="Comments"
          data-test-id="show-comments-button"
        >
          <Button
            kind="ghost"
            iconReverse
            icon={<FontAwesomeIcon icon={faComment} />}
          >
            Show comments
          </Button>
        </DisplayToggleWrapper>

        <Button
          kind="ghost"
          iconReverse
          icon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
          href={guidanceLink}
          target="_blank"
          title="ACR Guidance link"
          data-test-id="ACR Guidance link"
          onClick={() =>
            sendGAEvent('acr_guidance_link_clicked', { wfp_position: 'header' })
          }
        >
          ACR Guidance
        </Button>
        {dataSourceRefreshAllowed &&
          hasReport &&
          reportState !== 'Published' && (
            <RefreshDataSourceButton
              isFetching={dataSourceRefresh?.isFetching}
              handleRefreshDataSources={handleRefreshDataSources}
            />
          )}
      </ModuleBody>
    </Module>
  );
};

export const mapStateToProps = (state: RootState) => {
  const sectionData = state.viewData?.[SECTION_DETAIL_DATA_KEY]?.data;

  return {
    hasNarrative: sectionData?.narrative,
    guidanceLink: sectionData?.guidance_link,
    activeSectionID: state.viewState.activeSectionID,
    activeClickedTab: state.viewState.activeClickedTab,
    reportState: state.viewData?.[REPORT_DETAIL_DATA_KEY]?.data?.state,
    hasReport: !!state.viewData?.[REPORT_DETAIL_DATA_KEY]?.data,
    dataSourceRefreshAllowed: sectionData?.data_source_refresh_allowed,
  };
};

const SectionControls = connect(mapStateToProps)(SectionControlsComponent);

export default SectionControls;
